import apiService from "./api";

export enum RitualStatus {
	SCHEDULED = "SCHEDULED",
	IN_PROGRESS = "IN_PROGRESS",
	COMPLETED = "COMPLETED",
	CANCELLED = "CANCELLED",
	CLOSED = "CLOSED",
}

export interface Ritual {
	id: string;
	title: string;
	description?: string;
	ritualTypeId: string;
	status: RitualStatus;
	startDate: string;
	endDate?: string | null;
	location?: string;
	isPublic?: boolean;
	acceptsReservations?: boolean;
	publicNotes?: string | null;
	createdById: string;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	ritualType?: {
		id: string;
		name: string;
		code: string;
		spiritualLine?: {
			id: string;
			name: string;
			icon?: string;
			primaryColor?: string;
		};
		attendanceCards?: Array<{
			id: string;
			attendanceTypeId: string;
		}>;
	};
	createdBy?: {
		id: string;
		account: {
			email: string;
			displayName: string;
		};
	};
	_count?: {
		tickets: number;
	};
	_ticketStatusSummary?: {
		total: number;
		pending: number;
		called: number;
		allCompleted: boolean;
	};
}

export interface CreateRitualData {
	title: string;
	description?: string;
	ritualTypeId: string;
	status?: RitualStatus;
	startDate: string;
	location?: string;
	isPublic?: boolean;
	acceptsReservations?: boolean;
	publicNotes?: string;
}

export interface UpdateRitualData {
	title?: string;
	description?: string;
	ritualTypeId?: string;
	status?: RitualStatus;
	startDate?: string;
	endDate?: string;
	location?: string;
	isPublic?: boolean;
	acceptsReservations?: boolean;
	publicNotes?: string;
}

export interface UpdateRitualStatusData {
	status: RitualStatus;
	startedAt?: string;
}

export interface FindRitualsParams {
	page?: number;
	limit?: number;
	search?: string;
	ritualTypeId?: string;
	status?: RitualStatus;
	startDateFrom?: string;
	startDateTo?: string;
	spiritualLineId?: string;
	isActive?: boolean;
	sortBy?: string;
	sortOrder?: "asc" | "desc";
}

export interface PaginatedRituals {
	data: Ritual[];
	meta: {
		total: number;
		page: number;
		limit: number;
		pages: number;
	};
}

export interface RitualStats {
	total: number;
	active: number;
	inactive: number;
	byStatus: {
		scheduled: number;
		inProgress: number;
		completed: number;
		cancelled: number;
		closed: number;
	};
}

/**
 * Serviço para gerenciar rituais
 */
export default {
	/**
	 * Obtém lista de rituais com filtros e paginação
	 */
	async getRituals(params?: FindRitualsParams): Promise<PaginatedRituals> {
		const response = await apiService.get("/rituals", { params });
		return response;
	},

	/**
	 * Obtém um ritual específico por ID
	 */
	async getRitualById(id: string): Promise<Ritual> {
		const response = await apiService.get(`/rituals/${id}`);
		return response;
	},

	/**
	 * Cria um novo ritual
	 */
	async createRitual(data: CreateRitualData): Promise<Ritual> {
		const response = await apiService.post("/rituals", data);
		return response;
	},

	/**
	 * Atualiza um ritual existente
	 */
	async updateRitual(id: string, data: UpdateRitualData): Promise<Ritual> {
		const response = await apiService.put(`/rituals/${id}`, data);
		return response;
	},

	/**
	 * Atualiza apenas o status de um ritual
	 */
	async updateRitualStatus(
		id: string,
		data: UpdateRitualStatusData
	): Promise<Ritual> {
		const response = await apiService.patch(`/rituals/${id}/status`, data);
		return response;
	},

	/**
	 * Exclui um ritual (soft delete)
	 */
	async deleteRitual(id: string): Promise<Ritual> {
		const response = await apiService.delete(`/rituals/${id}`);
		return response;
	},

	/**
	 * Obtém estatísticas dos rituais
	 */
	async getRitualStats(): Promise<RitualStats> {
		const response = await apiService.get("/rituals/stats");
		return response;
	},

	/**
	 * Verifica se há um ritual ativo
	 */
	async hasActiveRitual(): Promise<boolean> {
		const response = await apiService.get("/rituals/has-active-ritual");
		return response.hasActiveRitual;
	},

	/**
	 * Obtém o ritual ativo atual
	 */
	async getActiveRitual(): Promise<Ritual | null> {
		const response = await apiService.get("/rituals/active-ritual");
		return response;
	},

	/**
	 * Obtém rituais por período específico (para calendário)
	 */
	async getRitualsByDateRange(
		startDateFrom: string,
		startDateTo: string
	): Promise<Ritual[]> {
		const response = await apiService.get("/rituals", {
			params: {
				startDateFrom,
				startDateTo,
				isActive: true,
				limit: 1000,
			},
		});
		return response.data;
	},

	async getTicketsForClosing(ritualId: string): Promise<ClosingTicket[]> {
		const response = await apiService.get(`/rituals/${ritualId}/tickets-for-closing`);
		return response.tickets;
	},

	async closeRitual(
		ritualId: string,
		data: CloseRitualData
	): Promise<CloseRitualResult> {
		return apiService.post(`/rituals/${ritualId}/close`, data);
	},

	/**
	 * Mensagens criadas neste ritual (qualquer status)
	 */
	async getRitualMessages(ritualId: string): Promise<RitualMessagesResult> {
		return apiService.get(`/rituals/${ritualId}/messages`);
	},

	/**
	 * Retornos vinculados a este ritual: criados nele e cumpridos nele
	 */
	async getRitualReturns(ritualId: string): Promise<RitualReturnsResult> {
		return apiService.get(`/rituals/${ritualId}/returns`);
	},

	/**
	 * Baixa o PDF em lista das mensagens do ritual
	 */
	async downloadMessagesPdf(ritualId: string): Promise<void> {
		await downloadRitualPdf(`/rituals/${ritualId}/messages/pdf`, `mensagens-${ritualId.slice(0, 8)}.pdf`);
	},

	/**
	 * Baixa o PDF em lista dos retornos do ritual
	 */
	async downloadReturnsPdf(ritualId: string): Promise<void> {
		await downloadRitualPdf(`/rituals/${ritualId}/returns/pdf`, `retornos-${ritualId.slice(0, 8)}.pdf`);
	},

	/**
	 * Baixa o XLSX em lista dos retornos do ritual, respeitando a ordenação escolhida
	 */
	async downloadReturnsXlsx(ritualId: string, sort: "created" | "target" | "alpha"): Promise<void> {
		await downloadRitualXlsx(
			`/rituals/${ritualId}/returns/xlsx?sort=${sort}`,
			`retornos-${ritualId.slice(0, 8)}.xlsx`,
		);
	},
};

async function downloadRitualPdf(url: string, filename: string): Promise<void> {
	const axios = apiService.getAxiosInstance();
	const response = await axios.get(url, { responseType: "blob" });
	const blob = new Blob([response.data], { type: "application/pdf" });
	const objectUrl = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = objectUrl;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(objectUrl);
}

async function downloadRitualXlsx(url: string, filename: string): Promise<void> {
	const axios = apiService.getAxiosInstance();
	const response = await axios.get(url, { responseType: "blob" });
	const objectUrl = URL.createObjectURL(response.data as Blob);
	const link = document.createElement("a");
	link.href = objectUrl;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(objectUrl);
}

export interface ClosingTicket {
	id: string;
	fullCode: string;
	position: number;
	status: string;
	person: {
		id: string;
		firstName: string;
		lastName: string;
		nickname?: string;
	};
	attendanceCard?: {
		id: string;
		attendanceType: {
			id: string;
			code: string;
			label: string;
			numberingMode?: string;
			hasQueue?: boolean;
		};
		medium?: {
			id: string;
			firstName: string;
			lastName: string;
			nickname?: string;
		};
		guide?: {
			id: string;
			name: string;
		};
	};
	pendingReturns?: Array<{
		id: string;
		returnType: string;
		totalCount?: number;
		remainingCount?: number;
		targetAttendanceType?: { id: string; code: string; label: string };
		targetRitualType?: { id: string; name: string };
		targetMedium?: { id: string; firstName: string; lastName: string };
		targetGuide?: { id: string; name: string };
		notes?: string;
	}>;
}

interface RitualHeaderInfo {
	id: string;
	title: string;
	startDate: string;
	status: string;
	ritualType: { name: string } | null;
}

interface ListPerson {
	id: string;
	firstName: string;
	lastName: string;
	nickname?: string | null;
	photoUrl?: string | null;
}

export interface RitualMessage {
	id: string;
	type: "ALERT" | "PENDENCY" | "NOTE" | "REPLENISHMENT";
	severity: "LOW" | "NORMAL" | "CRITICAL";
	message: string;
	isActive: boolean;
	resolvedAt: string | null;
	createdAt: string;
	person: ListPerson;
	createdBy: { id: string; account: { displayName: string } } | null;
}

export interface RitualReturn {
	id: string;
	returnType: "NEXT_RITUAL" | "SPECIFIC_RITUAL" | "SPECIFIC_DATE" | "COUNTER" | "INDEFINITE";
	totalCount: number | null;
	remainingCount: number | null;
	fulfilled: boolean;
	fulfilledAt: string | null;
	notes: string | null;
	createdAt: string;
	person: ListPerson;
	targetMedium: ListPerson | null;
	targetGuide: { id: string; name: string } | null;
	targetAttendanceType: { id: string; code: string; label: string } | null;
	targetRitualType: { id: string; name: string } | null;
}

export interface RitualMessagesResult {
	ritual: RitualHeaderInfo;
	messages: RitualMessage[];
}

export interface RitualReturnsResult {
	ritual: RitualHeaderInfo;
	newReturns: RitualReturn[];
	fulfilledReturns: RitualReturn[];
	markedReturns: RitualReturn[];
}

export interface ClosingMessageData {
	type: "ALERT" | "PENDENCY" | "NOTE" | "REPLENISHMENT";
	severity?: "LOW" | "NORMAL" | "CRITICAL";
	message: string;
}

export interface ClosingReturnData {
	returnType: "NEXT_RITUAL" | "SPECIFIC_RITUAL" | "SPECIFIC_CARD";
	targetMediumId?: string;
	targetGuideId?: string;
	targetAttendanceTypeId?: string;
	targetRitualTypeId?: string;
	targetRitualId?: string;
	referredByGuideId?: string;
	totalCount?: number;
	indefinite?: boolean;
	notes?: string;
}

export interface ClosingItemData {
	ticketId: string;
	personId: string;
	messages?: ClosingMessageData[];
	return?: ClosingReturnData;
}

export interface CloseRitualData {
	closingData: ClosingItemData[];
	closedAt?: string;
}

export interface CloseRitualResult {
	ritual: Ritual;
	summary: {
		messagesCreated: number;
		returnsCreated: number;
	};
}
